import React, { useContext, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { FiArrowRight, FiBookOpen, FiCheckCircle, FiFeather } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../../../Context';
import { db } from '../../../../firebase/firebase';
import NavbarGlobal from '../../../NavbarGlobal';
import '../../../MaterialBiblioteca.scss';

const NIVELES_POSIBLES = [1, 2, 3];

const descripcionPorNivel = {
    1: 'Lecturas breves para afianzar comprensión, vocabulario y atención.',
    2: 'Textos para interpretar ideas, relacionar información y sacar conclusiones.',
    3: 'Lecturas avanzadas para analizar, argumentar y profundizar en el contenido.',
};

const ViewLecturasAlumnos = () => {
    const { currentUser, teacher } = useContext(AppContext);
    const navigate = useNavigate();
    const [nivelesDisponibles, setNivelesDisponibles] = useState([]);
    const [estadoCarga, setEstadoCarga] = useState('cargando');

    const tieneAccesoTotal = teacher || currentUser?.isMaster;

    useEffect(() => {
        let activo = true;

        const obtenerLecturasAsignadas = async () => {
            setEstadoCarga('cargando');

            if (tieneAccesoTotal) {
                if (activo) {
                    setNivelesDisponibles(NIVELES_POSIBLES);
                    setEstadoCarga('listo');
                }
                return;
            }

            if (!currentUser?.uid) {
                if (activo) {
                    setNivelesDisponibles([]);
                    setEstadoCarga('listo');
                }
                return;
            }

            try {
                const alumnoLecturasRef = doc(db, 'alumnosLecturas', currentUser.uid);
                const docSnap = await getDoc(alumnoLecturasRef);

                if (!activo) return;

                const lecturasAsignadas = docSnap.exists()
                    ? docSnap.data().lecturasAsignadas || []
                    : [];
                const nivelesAsignados = [...new Set(
                    lecturasAsignadas
                        .map((lectura) => Number.parseInt(
                            lectura.level?.toString().replace('Nivel ', ''),
                            10,
                        ))
                        .filter((nivel) => NIVELES_POSIBLES.includes(nivel)),
                )].sort((a, b) => a - b);

                setNivelesDisponibles(nivelesAsignados);
                setEstadoCarga('listo');
            } catch (error) {
                console.error('Error al obtener lecturas asignadas:', error);
                if (activo) {
                    setNivelesDisponibles([]);
                    setEstadoCarga('error');
                }
            }
        };

        obtenerLecturasAsignadas();

        return () => {
            activo = false;
        };
    }, [currentUser?.uid, tieneAccesoTotal]);

    const handleVerDetalle = (nivel) => {
        navigate(`/dashboard/lecturas-adicionales/detalle/${nivel}`);
    };

    return (
        <div className="material-library reading-library">
            <NavbarGlobal title="Lecturas para alumnos" showBackButton={true} />
            <main className="material-library__container">
                <section
                    className="material-library__hero reading-library__hero"
                    aria-labelledby="lecturas-alumnos-title"
                >
                    <div className="material-library__hero-copy">
                        <span className="material-library__eyebrow">
                            <FiFeather aria-hidden="true" /> Comprensión lectora
                        </span>
                        <h1 id="lecturas-alumnos-title">Cada lectura abre una nueva idea</h1>
                        <p>
                            Explora los textos asignados para tu proceso y pon a prueba lo que comprendiste.
                            {tieneAccesoTotal && ' Tienes acceso a todos los niveles.'}
                        </p>
                    </div>
                    <div className="material-library__hero-mark" aria-hidden="true">
                        <FiBookOpen />
                        <strong>{estadoCarga === 'cargando' ? '—' : nivelesDisponibles.length}</strong>
                        <span>{nivelesDisponibles.length === 1 ? 'nivel con lecturas' : 'niveles con lecturas'}</span>
                    </div>
                </section>

                {estadoCarga === 'cargando' ? (
                    <section className="material-library__loading" role="status" aria-live="polite">
                        <span className="material-library__loading-mark" aria-hidden="true" />
                        <div>
                            <h2>Preparando tus lecturas</h2>
                            <p>Estamos consultando el contenido asignado.</p>
                        </div>
                    </section>
                ) : nivelesDisponibles.length > 0 ? (
                    <section className="material-library__section" aria-labelledby="niveles-lectura-title">
                        <div className="material-library__section-heading">
                            <div>
                                <span className="material-library__section-kicker">Tu biblioteca</span>
                                <h2 id="niveles-lectura-title">Elige un nivel de lectura</h2>
                            </div>
                            <span className="material-library__access-note">
                                <FiCheckCircle aria-hidden="true" /> Lecturas asignadas
                            </span>
                        </div>

                        <div className="material-level-grid">
                            {nivelesDisponibles.map((nivel) => (
                                <button
                                    key={nivel}
                                    type="button"
                                    className="material-level-card material-level-card--reading"
                                    onClick={() => handleVerDetalle(nivel)}
                                    aria-label={`Ver lecturas asignadas del nivel ${nivel}`}
                                >
                                    <span className="material-level-card__tab">Nivel {nivel}</span>
                                    <span className="material-level-card__icon" aria-hidden="true">
                                        <FiBookOpen />
                                    </span>
                                    <span className="material-level-card__body">
                                        <strong>Lecturas del nivel {nivel}</strong>
                                        <span>{descripcionPorNivel[nivel]}</span>
                                    </span>
                                    <span className="material-level-card__action">
                                        Ver mis lecturas <FiArrowRight aria-hidden="true" />
                                    </span>
                                </button>
                            ))}
                        </div>
                    </section>
                ) : (
                    <section className="material-library__empty" role="status">
                        <span className="material-library__empty-icon"><FiBookOpen aria-hidden="true" /></span>
                        <h2>{estadoCarga === 'error' ? 'No pudimos cargar tus lecturas' : 'Aún no tienes lecturas asignadas'}</h2>
                        <p>{estadoCarga === 'error'
                            ? 'Revisa tu conexión e intenta ingresar nuevamente.'
                            : 'Cuando tu profesor asigne una lectura, aparecerá organizada por nivel en este espacio.'}</p>
                    </section>
                )}
            </main>
        </div>
    );
};

export { ViewLecturasAlumnos };
